"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export function AuditWebsiteBuilder({ section }) {
  return (
    <section className="py-20 relative overflow-visible bg-transparent w-full font-sans">
      {/* Wavy Backdrop Shape matching Odoo's curved background layout (fully transparent, no blur) */}
      <div
        className="absolute top-0 left-0 w-full lg:w-[92%] h-[72%] bg-white/5 -z-10 shadow-2xl"
        style={{
          borderBottomRightRadius: "360px",
          borderTopRightRadius: "360px",
        }}
      />

      {/* Light Bulb Icon - overlaying the top edge of the curved backdrop exactly, hue-rotated to theme orange */}
      <div className="absolute top-[-50px] left-[10%] z-20 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -20, rotate: -25 }}
          whileInView={{ opacity: 1, y: 0, rotate: -15 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative h-[120px] w-[120px]"
        >
          <Image
            src={section.bulbIcon}
            style={{ filter: "hue-rotate(25deg) saturate(1.8) brightness(1.1)" }}
            fill
            className="-rotate-[15deg] transform origin-bottom-left object-contain"
            alt="Orange theme light bulb illustration"
          />
        </motion.div>
      </div>

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        <div className="grid lg:grid-cols-12 gap-12 items-center pb-6">
          {/* Left Column: Heading and Text description */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-6"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-none">
              <span className="relative inline-block mr-3">
                <span className="relative z-10 text-white">AI-based</span>
                {/* Theme Orange Marker hand-drawn double loop underline outline */}
                <svg className="absolute -bottom-2 left-0 w-full h-4 text-[#f1833b] overflow-visible" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path
                    d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8 C 25 6, 65 5, 95 7"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className={`block lg:inline ${caveat.className} text-white text-5xl md:text-7xl font-bold`}>
                website builder
              </span>
            </h2>

            <p
              className="text-gray-300 leading-relaxed font-normal pt-4 text-xl"
              dangerouslySetInnerHTML={{ __html: section.mainText }}
            />
          </motion.div>

          {/* Right Column: Premium borderless & shadowless image mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 flex justify-center lg:justify-end"
          >
            <div className="w-full max-w-[580px] transition-transform hover:scale-[1.01] duration-300 relative">
              <Image
                src={section.image}
                width={580}
                height={400}
                className="w-full h-auto rounded-[16px] object-contain"
                alt="Odoo Audit Website Template preview"
              />
            </div>
          </motion.div>
        </div>

        {/* Lower Two Columns for contacts and domain lists */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-16 pt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p
              className="text-gray-300 leading-relaxed font-normal text-xl"
              dangerouslySetInnerHTML={{ __html: section.bottomTextLeft }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p
              className="text-gray-300 leading-relaxed font-normal text-xl"
              dangerouslySetInnerHTML={{ __html: section.bottomTextRight }}
            />
          </motion.div>
        </div>

      </div>
    </section>
  );
}

export function AuditLeadManagement({ section }) {
  return (
    <section className="py-24 relative overflow-visible bg-transparent w-full font-sans">
      {/* Dashed grey hand-drawn arrow pointing down and to the left, connecting from the top section */}
      <div className="absolute top-[-80px] left-[45%] lg:left-[48%] z-20 pointer-events-none hidden lg:block opacity-35 select-none">
        <Image
          src="/odoo/arrows/grey_arrow_loop_02.svg"
          width={140}
          height={140}
          className="rotate-[35deg] scale-x-[-1] brightness-0 invert object-contain"
          alt="Hand drawn arrow connection"
        />
      </div>

      {/* Sharp, visible solid background circle behind mockup matching Odoo's x_wd_shape_circle */}
      <div className="absolute top-[8%] lg:top-[12%] right-[-8%] lg:right-[-3%] w-[480px] sm:w-[560px] h-[480px] sm:h-[560px] bg-white/[0.03] rounded-full -z-10 pointer-events-none select-none" />

      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">

          {/* Left Column: Easy lead management copy */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">
              <span className={`relative inline-block mr-3 ${caveat.className} text-white text-5xl md:text-7xl font-bold`}>
                <span className="relative z-10">{section.titleHighlight}</span>
                {/* Theme Orange hand-drawn double loop underline highlight */}
                <svg className="absolute -bottom-2 left-0 w-full h-4 text-[#f1833b] overflow-visible" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path
                    d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8 C 25 6, 65 5, 95 7"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="block lg:inline text-white">
                {section.titleRest}
              </span>
            </h2>

            <div className="space-y-6 pt-4">
              {section.paragraphs?.map((para, i) => (
                <p
                  key={i}
                  className="text-gray-300 leading-relaxed font-normal text-xl"
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </div>
          </motion.div>

          {/* Right Column: CRM pipeline template mockup, offset by 1 column for breathable grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 lg:offset-1 flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-[580px]">
              <svg
                className="absolute -top-[64px] -right-[64px] w-24 h-24 pointer-events-none select-none z-20"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Ray 1: near vertical */}
                <path
                  d="M 40 60 C 45 45, 50 30, 55 10"
                  stroke="#f1833b"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                {/* Ray 2: diagonal */}
                <path
                  d="M 45 65 C 58 52, 72 38, 85 25"
                  stroke="#f1833b"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                {/* Ray 3: lower diagonal */}
                <path
                  d="M 50 70 C 68 62, 85 54, 100 45"
                  stroke="#f1833b"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                {/* Ray 4: horizontal */}
                <path
                  d="M 52 75 C 72 75, 92 75, 110 75"
                  stroke="#f1833b"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
              <Image
                src={section.image}
                width={580}
                height={400}
                className="w-full h-auto rounded-[16px] object-contain relative z-10"
                alt="Odoo Audit Lead Management pipeline"
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

export function AuditMobileSolution({ section }) {
  return (
    <section className="py-20 relative overflow-visible bg-transparent w-full font-sans">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1200px] relative z-10">

        {/* Vibrating Phone Icon at the top - hidden on mobile, visible on desktop matching Odoo's exact behavior */}
        <div className="hidden lg:block relative justify-center mb-8 w-fit mx-auto">
          {/* Soft Orange/Red accent highlight behind phone icon to give premium depth */}
          <div className="absolute inset-0 bg-[#f1833b]/15 blur-2xl rounded-full scale-110 -z-10 pointer-events-none select-none" />

          {/* Orange highlighter stroke behind/under the vibrate icon */}
          <svg className="absolute bottom-[-12px] left-[15%] w-[70%] h-4 text-[#f1833b] overflow-visible opacity-90" viewBox="0 0 100 10" preserveAspectRatio="none">
            <path
              d="M 5 5 C 30 2, 70 3, 95 4 C 75 6, 35 7, 10 5"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
            />
          </svg>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="h-[140px] w-[140px] relative mx-auto"
          >
            <Image
              src={section.icon}
              style={{ filter: "hue-rotate(205deg) saturate(1.8) brightness(1)" }}
              fill
              className="mx-auto select-none object-contain"
              alt="Mobile solution vibration icon"
            />
          </motion.div>
        </div>

        {/* Centered Heading */}
        <div className="text-center max-w-[900px] mx-auto mb-12">
          <h2 className="text-4xl md:text-[56px] font-extrabold text-white tracking-tight leading-tight text-center">
            <span className="relative inline-block mr-3">
              <span className={`relative z-10 text-white ${caveat.className} text-5xl md:text-7xl font-bold`}>
                {section.titleHighlight}
              </span>
              {/* Odoo Theme Orange Underline (x_wd_green_highlight_bold_01 updated to theme) */}
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-[#f1833b] overflow-visible opacity-95" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path
                  d="M 2 8 C 30 4, 70 3, 98 5 C 75 7, 35 9, 5 8 C 25 6, 65 5, 95 7"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="text-white block lg:inline">
              {section.titleRest}
            </span>
          </h2>
        </div>

        {/* Two Column Paragraphs */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 max-w-[1000px] mx-auto mb-16 justify-center">
          {section.paragraphs?.map((para, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
            >
              <p
                className="text-gray-300 leading-relaxed font-normal text-xl"
                dangerouslySetInnerHTML={{ __html: para }}
              />
            </motion.div>
          ))}
        </div>

        {/* Same to Same Grid Flow matching Odoo's exact markup - fully responsive in a single block */}
        <div className="relative py-12 flex justify-center items-center">

          {/* Centered background circle matching x_wd_shape_circle_sm */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] sm:w-[540px] lg:w-[700px] h-[320px] sm:h-[540px] lg:h-[700px] bg-white/5 rounded-full -z-10 pointer-events-none select-none" />

          {/* Row element */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-14 max-w-[940px] w-full mx-auto">

            {/* Left Column (col-lg-3): Map Screen (top), Doodle 1 (middle), and Task Screen (bottom) - naturally LEFT on desktop */}
            <div className="w-[85%] sm:w-[70%] md:w-[32%] md:max-w-[290px] flex flex-col select-none">

              {/* audit-1.webp: Map card */}
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="py-3"
              >
                <Image
                  src={section.images.map}
                  width={290}
                  height={200}
                  className="p-1 bg-white border border-gray-200 rounded-[8px] shadow-xl w-full mx-auto object-contain hover:scale-[1.02] transition-transform duration-300"
                  alt="Navigate to location map"
                />
              </motion.div>

              {/* Doodle 1: Navigate to location (arrow pointing up from Task to Map) */}
              <div className="text-center my-3">
                <em className={`not-italic text-[#b48ead] ${caveat.className} text-xl lg:text-2xl font-semibold flex flex-col items-center leading-tight`}>
                  <div className="hidden md:block mx-auto ml-6 mb-1 rotate-[220deg] brightness-0 invert opacity-60 relative w-[71px] h-[71px]">
                    <Image
                      src={section.arrows.greyArrow}
                      fill
                      className="object-contain"
                      alt=""
                    />
                  </div>
                  Navigate to location
                </em>
              </div>

              {/* audit-3.webp: Task card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Image
                  src={section.images.task}
                  width={290}
                  height={200}
                  className="p-1 bg-white border border-gray-200 rounded-[8px] shadow-xl w-full mx-auto object-contain hover:scale-[1.02] transition-transform duration-300"
                  alt="Drag and drop task card"
                />
              </motion.div>

            </div>

            {/* Right Column (col-lg-3): Doodle 2 (top), Form Card (middle), and Doodle 3 (bottom) - naturally RIGHT on desktop */}
            <div className="w-[85%] sm:w-[70%] md:w-[32%] md:max-w-[290px] flex flex-col justify-center select-none">

              {/* Doodle 2: Fill in certification data tasks (arrow pointing down-right from Map to Form) */}
              <div className="text-center mb-3">
                <em className={`not-italic text-[#b48ead] ${caveat.className} text-xl lg:text-2xl font-semibold flex flex-col items-center leading-tight`}>
                  <span>Fill in certification data</span>
                  <span>tasks</span>
                  <div className="hidden md:block mx-auto mt-2 rotate-[300deg] brightness-0 invert opacity-60 relative w-[71px] h-[71px]">
                    <Image
                      src={section.arrows.greyArrow}
                      fill
                      className="object-contain"
                      alt=""
                    />
                  </div>
                </em>
              </div>

              {/* audit-2.webp: Form card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Image
                  src={section.images.form}
                  width={290}
                  height={350}
                  className="p-1 bg-white border border-gray-200 rounded-[8px] shadow-xl w-full mx-auto object-contain hover:scale-[1.02] transition-transform duration-300"
                  alt="Fill in certification data tasks sheet"
                />
              </motion.div>

              {/* Doodle 3: Drag-and-drop to update tasks (arrow pointing down-left from Form to Task) */}
              <div className="text-center my-3">
                <em className={`not-italic text-[#b48ead] ${caveat.className} text-xl lg:text-2xl font-semibold flex flex-col items-center leading-tight`}>
                  <div className="hidden md:block mx-auto mb-1 ml-6 rotate-[60deg] brightness-0 invert opacity-60 relative w-[71px] h-[71px]">
                    <Image
                      src={section.arrows.greyArrow}
                      fill
                      className="object-contain"
                      alt=""
                    />
                  </div>
                  <span>Drag-and-drop to update</span>
                  <span>tasks</span>
                </em>
              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
}
